import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;

/* ─── Injected styles ───────────────────────────────────────────────────── */
const STYLE_ID = "spec-details-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes specFadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes specShimmer {
      0%   { background-position: -700px 0; }
      100% { background-position: 700px 0; }
    }

    /* ── Rich HTML content ── */
    .spec-html-content h1,
    .spec-html-content h2,
    .spec-html-content h3,
    .spec-html-content h4,
    .spec-html-content h5,
    .spec-html-content h6 {
      font-weight: 700; line-height: 1.3; margin: 1.4em 0 0.5em; color: #0f172a;
    }
    .spec-html-content h1 { font-size: 2rem; }
    .spec-html-content h2 { font-size: 1.6rem; }
    .spec-html-content h3 { font-size: 1.35rem; }
    .spec-html-content h4 { font-size: 1.15rem; }
    .spec-html-content p  { margin: 0 0 1.1em; line-height: 1.8; color: #334155; }
    .spec-html-content ul,
    .spec-html-content ol { margin: 0 0 1.1em 1.5em; padding: 0; color: #334155; line-height: 1.8; }
    .spec-html-content li { margin-bottom: 0.35em; }
    .spec-html-content blockquote {
      border-left: 4px solid #007ebb; margin: 1.5em 0; padding: 0.75em 1.2em;
      background: #f0f7ff; color: #334155; font-style: italic;
    }
    .spec-html-content strong { font-weight: 700; }
    .spec-html-content em    { font-style: italic; }
    .spec-html-content a     { color: #007ebb; text-decoration: underline; }
    .spec-html-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
    .spec-html-content th,
    .spec-html-content td   { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; }
    .spec-html-content th   { background: #f1f5f9; font-weight: 600; }
    .spec-html-content hr   { border: none; border-top: 1px solid #e2e8f0; margin: 2em 0; }
    .spec-html-content img  { max-width: 100%; border-radius: 8px; }

    /* ── Doctors grid ── */
    .spec-doctors-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 24px;
    }
    @media (max-width: 1199px) { .spec-doctors-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
    @media (max-width: 767px)  { .spec-doctors-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; } }
    @media (max-width: 479px)  { .spec-doctors-grid { grid-template-columns: 1fr; } }

    .spec-doctor-card {
      border-radius: 14px; overflow: hidden; background: #fff;
      box-shadow: 0 4px 20px rgba(0,107,187,0.09);
      text-decoration: none; display: block;
      transition: transform 0.28s cubic-bezier(0.22,1,0.36,1), box-shadow 0.28s ease;
    }
    .spec-doctor-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 14px 36px rgba(0,107,187,0.18);
    }
    .spec-doctor-card__photo {
      width: 100%; aspect-ratio: 3/4; object-fit: cover; object-position: top center;
      display: block; background: linear-gradient(135deg, #007ebb, #0d3b6e);
    }
    .spec-doctor-card__initials {
      width: 100%; aspect-ratio: 3/4; display: flex; align-items: center; justify-content: center;
      background: linear-gradient(145deg, #007ebb, #0d3b6e);
      font-size: 2rem; font-weight: 800; color: #fff; letter-spacing: 2px;
    }
    .spec-doctor-card__body {
      padding: 16px 14px 18px; text-align: center;
    }
    .spec-doctor-card__name {
      font-size: 13.5px; font-weight: 700; color: #1f2d3d; margin: 0 0 5px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .spec-doctor-card__role {
      font-size: 12px; color: #007ebb; font-weight: 600;
    }

    /* ── Gallery ── */
    .spec-gallery { display: grid; gap: 14px; }
    .spec-gallery--2 { grid-template-columns: repeat(2, 1fr); }
    .spec-gallery--3 { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 600px) {
      .spec-gallery--2, .spec-gallery--3 { grid-template-columns: 1fr; }
    }
    .spec-gallery__item {
      border-radius: 12px; overflow: hidden;
      box-shadow: 0 4px 18px rgba(0,0,0,0.1);
      aspect-ratio: 4/3;
    }
    .spec-gallery__item img {
      width: 100%; height: 100%; object-fit: cover; display: block;
      transition: transform 0.4s ease;
    }
    .spec-gallery__item:hover img { transform: scale(1.04); }

    /* ── Section label ── */
    .spec-section-label {
      display: inline-flex; align-items: center; gap: 8px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
      text-transform: uppercase; color: #007ebb; margin-bottom: 16px;
    }
    .spec-section-label::before {
      content: ''; width: 28px; height: 2px; background: #007ebb; border-radius: 2px;
    }

    /* ── Shimmer ── */
    .spec-shimmer {
      background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
      background-size: 700px 100%;
      animation: specShimmer 1.5s ease-in-out infinite;
      border-radius: 10px;
    }
  `;
  document.head.appendChild(style);
}

/* ─── Blob image hook ───────────────────────────────────────────────────── */
function useBlobImage(url) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    let objectUrl = null;
    setLoading(true);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, loading };
}

/* ─── Doctor card ───────────────────────────────────────────────────────── */
function DoctorCard({ doctor, delay }) {
  const { src, loading: imgLoading } = useBlobImage(doctor.hasImage ? `${BASE}/website/doctor/${doctor.id}/image` : null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const initials = (doctor.name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, animation: visible ? `specFadeUp 0.5s ${delay}s ease both` : "none" }}>
      <Link to={`/doctor/${doctor.id}`} className="spec-doctor-card">
        {imgLoading ? (
          <div className="spec-shimmer" style={{ aspectRatio: "3/4" }} />
        ) : src ? (
          <img src={src} alt={doctor.name} className="spec-doctor-card__photo" />
        ) : (
          <div className="spec-doctor-card__initials">{initials}</div>
        )}
        <div className="spec-doctor-card__body">
          <p className="spec-doctor-card__name" title={doctor.name}>
            {doctor.name}
          </p>
          <span className="spec-doctor-card__role">{doctor.designation || doctor.speciality || "Consultant"}</span>
        </div>
      </Link>
    </div>
  );
}

/* ─── Gallery image ─────────────────────────────────────────────────────── */
function GalleryImage({ url, alt }) {
  const { src, loading } = useBlobImage(url);
  if (loading) return <div className="spec-shimmer spec-gallery__item" />;
  if (!src) return null;
  return (
    <div className="spec-gallery__item">
      <img src={src} alt={alt} />
    </div>
  );
}

/* ═══ MAIN ═════════════════════════════════════════════════════════════════ */
const SpecialityDetailsMain = () => {
  const { id } = useParams();
  const [speciality, setSpeciality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { src: heroSrc, loading: heroLoading } = useBlobImage(speciality?.hasImage ? `${BASE}/website/speciality/${id}/image` : null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    makeRequest({ route: `/website/speciality/public/${id}`, method: "get" })
      .then((res) => setSpeciality(res?.data || null))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main>
        <Breadcrumb title="Speciality" subTitle="Specialities" />
        <div style={{ padding: "80px 0", textAlign: "center" }}>
          <div className="spec-shimmer" style={{ width: 120, height: 20, margin: "0 auto" }} />
        </div>
      </main>
    );
  }

  if (error || !speciality) {
    return (
      <main>
        <Breadcrumb title="Speciality" subTitle="Specialities" />
        <div style={{ padding: "80px 0", textAlign: "center", color: "#ef4444" }}>Speciality not found.</div>
      </main>
    );
  }

  const doctors = speciality.doctors || [];
  const galleryImages = [
    speciality.hasImage2 ? { url: `${BASE}/website/speciality/${id}/image2`, key: "image2" } : null,
    speciality.hasImage3 ? { url: `${BASE}/website/speciality/${id}/image3`, key: "image3" } : null,
  ].filter(Boolean);

  return (
    <main>
      <Breadcrumb title={speciality.name} subTitle="Specialities" />

      {/* ── Hero ── */}
      <section style={{ background: "#fff", paddingTop: 70, paddingBottom: 56 }}>
        <div className="container">
          <div className="row align-items-center">
            {/* Hero image */}
            {(heroLoading || heroSrc) && (
              <div className="col-xl-5 col-lg-5 mb-40">
                <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 36px rgba(0,107,187,0.15)", position: "relative" }}>
                  {heroLoading ? (
                    <div className="spec-shimmer" style={{ width: "100%", paddingBottom: "72%" }} />
                  ) : (
                    <img src={heroSrc} alt={speciality.name} style={{ width: "100%", display: "block", objectFit: "cover" }} />
                  )}
                </div>
              </div>
            )}

            {/* Title + description */}
            <div className={heroSrc || heroLoading ? "col-xl-7 col-lg-7 mb-40" : "col-12 mb-40"}>
              <div style={{ paddingLeft: heroSrc || heroLoading ? 24 : 0 }}>
                <span className="spec-section-label">Clinical Speciality</span>
                <h1 style={{ fontSize: "clamp(26px,3.5vw,42px)", fontWeight: 800, color: "#0d1b2a", lineHeight: 1.2, marginBottom: 18 }}>
                  {speciality.icon && <i className={speciality.icon} style={{ marginRight: 14, color: "#007ebb", fontSize: "0.85em" }} />}
                  {speciality.name}
                </h1>
                {speciality.description && (
                  <p style={{ fontSize: 17, color: "#475569", lineHeight: 1.8, marginBottom: 28, maxWidth: 560 }}>{speciality.description}</p>
                )}
                <Link
                  to="/appointment"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#007ebb",
                    color: "#fff",
                    padding: "13px 28px",
                    borderRadius: 50,
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    boxShadow: "0 6px 22px rgba(0,126,187,0.32)",
                    transition: "background 0.2s",
                  }}
                >
                  Book an Appointment
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" y1="10" x2="16" y2="10" />
                    <polyline points="10 4 16 10 10 16" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HTML Content ── */}
      {speciality.htmlContent && (
        <section style={{ background: "#f8fbfd", paddingTop: 60, paddingBottom: 60 }}>
          <div className="container">
            <span className="spec-section-label">About This Speciality</span>
            <div className="spec-html-content" style={{ maxWidth: 860 }} dangerouslySetInnerHTML={{ __html: speciality.htmlContent }} />
          </div>
        </section>
      )}

      {/* ── Gallery ── */}
      {galleryImages.length > 0 && (
        <section style={{ background: "#fff", paddingTop: 60, paddingBottom: 60 }}>
          <div className="container">
            <span className="spec-section-label">Gallery</span>
            <div className={`spec-gallery spec-gallery--${galleryImages.length}`}>
              {galleryImages.map((img) => (
                <GalleryImage key={img.key} url={img.url} alt={`${speciality.name} gallery`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Doctors ── */}
      <section style={{ background: galleryImages.length > 0 ? "#f8fbfd" : "#fff", paddingTop: 60, paddingBottom: 80 }}>
        <div className="container">
          <span className="spec-section-label">Our Team</span>
          <div
            style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 36, flexWrap: "wrap", gap: 12 }}
          >
            <h2 style={{ fontSize: "clamp(22px,2.8vw,34px)", fontWeight: 800, color: "#0d1b2a", margin: 0 }}>{speciality.name} Specialists</h2>
            {doctors.length > 0 && (
              <Link
                to="/our-doctors"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#007ebb",
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                }}
              >
                View All Doctors
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="4" y1="10" x2="16" y2="10" />
                  <polyline points="10 4 16 10 10 16" />
                </svg>
              </Link>
            )}
          </div>

          {doctors.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="26" cy="20" r="10" />
                <path d="M6 46c0-11 9-18 20-18s20 7 20 18" />
              </svg>
              <p style={{ marginTop: 16, fontSize: 15 }}>No doctors listed for this speciality yet.</p>
            </div>
          ) : (
            <div className="spec-doctors-grid">
              {doctors.map((doc, i) => (
                <DoctorCard key={doc.id} doctor={doc} delay={Math.min(i * 0.06, 0.4)} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default SpecialityDetailsMain;
